
/*
 * Quantellia ODIModel
 *
 * Copyright (c) 2016 - 2025 Quantellia LLC
 * 
 *  All Rights Reserved.
 * 
 * NOTICE:  All source code, data, and any other information contained 
 * in this file is, and remains the property of Quantellia LLC.
 * The intellectual and technical concepts contained
 * herein are proprietary to Quantellia LLC and may be covered by
 * U.S. and Foreign Patents, patents in process, and are protected 
 * by trade secret or copyright law. Dissemination of this information 
 * or reproduction of this material is strictly forbidden unless 
 * prior written permission is obtained from Quantellia LLC.
 * 
 * The above notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 *
 */

import {createGUID} from "./qlib.js";

/**
 * Javascript represetnation of an Open Decision Intelligence
 */
export class ODIModel
{
    //
    // We build the model as  Javascript Object that we can then serialized to JSON
    //
    #model;

    /**
     * Constructor for initializing a new ODIModel object. All arguments are optional.
     * @param {string} name The name given to the model. Defaults to "New Open Decision Intelligence Model" if not defined.
     * @param {string} id  The ID given to the model. Defaults to a new GUID if not defined.
     * @param {Array} elements The array of DIModelElements contained in the model. Defaults to an empty array if not defined.
     * @param {string} summary The summary line of the model.  May be null.
     */
    constructor(name, id, elements, summary)
    {
        this.model = {
                        Name: name || "New Open Decision Intelligence Model",
                        ID: id || createGUID(),
                        Elements: elements || [],
                        Summary: summary
                    }
    };


    /**
     * Add a new element to the model.
     * @param {object} elem A Javascript object representing the model element to be added.
     * @returns {object} The Javascript object that was passed as the argument.
     * @description The object passed as the elem argument must have internal structure that is valid
     *               for the type of object being added.  It is the caller's responsibility to ensure this
     *               is the case.
     */
    addElement(elem)
    {
        this.model.Elements.push(elem);
        return elem;
    }

    /**
     * Create a new object that represents a DIModelDiagram instance and add it to the Elements array of this model. 
     * @param {string} name The name given to the diagram. Defaults to "New Open Decision Intelligence Diagram" if not defined.
     * @param {string} id  The ID given to the diagram. Defaults to a new GUID if not defined.
     * @param {string} summary The summary line of the diagram.  May be null.
     * @returns A reference to the newly created javascript object referencing the diagram.
     */
    addDiagram(id, name, elements, summary)
    {
        var diagram = {
                        Type: "DIModelDiagram",
                        ID: id || createGUID(),
                        Name: name || "New Open Decision Intelligence Diagram",
                        Summary: summary,
                        DiagramElements: elements || []
                    };

        this.addElement(diagram);

        return diagram;
    };


    /**
     * Translates the string representation of a role from another modeling platform to valid role
     * strings used by the ODI framework.
     * @param {string} role The role string from the source modeling system.  May be null or empty,
     * in which case "Intermediate" is assumed.
     * @returns One of the following strings: "Action","External","Intermediate","Outcome".
     */
    translateRole(role)
    {
        if (role)
        {
            switch (role.toUpperCase())
            {
              // TODO should LEVER be here?  What does it accomplish?
              // Are we allowing levers/actions to be synonyms for each other?
                case "ACTION":
                    return "Action";
                case "LEVER":
                    return "Action";

                case "EXTERNAL":
                    return "External";

                case "OUTCOME":
                    return "Outcome";
            }
        }

        //
        // This is the default value
        //
        return "Intermediate";
    }

    toJSON()
    {
        return JSON.stringify(this.model);
    }
}